import { type CountryCode, parsePhoneNumberWithError, type PhoneNumber } from "libphonenumber-js";

type PhoneInput = {
  value: string | null | undefined;
  /**
   * ISO alpha-2 code used to interpret a number written in national format.
   * Follows the address country; an unknown or absent code simply means a
   * national number cannot be judged.
   */
  defaultCountry?: string | null;
};

/**
 * Parses a phone number, tolerating anything libphonenumber-js rejects outright.
 *
 * A `+`-prefixed number is parsed on its own terms so an international number is
 * accepted whatever country the address is in.
 */
const parse = ({ value, defaultCountry }: PhoneInput): PhoneNumber | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) return undefined;

  try {
    return trimmed.startsWith("+")
      ? parsePhoneNumberWithError(trimmed)
      : parsePhoneNumberWithError(trimmed, { defaultCountry: defaultCountry as CountryCode | undefined });
  } catch {
    return undefined;
  }
};

/**
 * Whether a phone number is acceptable. Blank is acceptable — the field is
 * optional — and so is a national number we have no country to judge against.
 *
 * @example
 * isValidPhoneNumber({ value: "(212) 555-1234", defaultCountry: "US" }) // => true
 * isValidPhoneNumber({ value: "+44 20 7946 0958", defaultCountry: "US" }) // => true
 * isValidPhoneNumber({ value: "123", defaultCountry: "US" }) // => false
 */
export const isValidPhoneNumber = ({ value, defaultCountry }: PhoneInput): boolean => {
  const trimmed = value?.trim();

  if (!trimmed) return true;

  const phoneNumber = parse({ value: trimmed, defaultCountry });

  if (phoneNumber) return phoneNumber.isValid();

  // Nothing to parse against: a national number with no country is not wrong,
  // just unjudgeable. A `+`-prefixed number that failed to parse is malformed.
  return !trimmed.startsWith("+") && !defaultCountry;
};

/**
 * Normalises a phone number to E.164 for the API.
 *
 * @returns E.164 for a parseable number, the trimmed input when it cannot be
 *   parsed, or null when blank
 *
 * @example
 * toE164({ value: "(212) 555-1234", defaultCountry: "US" }) // => "+12125551234"
 * toE164({ value: "" }) // => null
 */
export const toE164 = ({ value, defaultCountry }: PhoneInput): string | null => {
  const trimmed = value?.trim();

  if (!trimmed) return null;

  return parse({ value: trimmed, defaultCountry })?.format("E.164") ?? trimmed;
};
